"use client";

import {
  storyblokEditable,
} from "@storyblok/react/rsc";
import React, { useEffect, useRef, useState } from "react";
import { cn, createSlug } from "@/lib/utils";
import { SectionStoryblok } from "@/component-types-sb";
import { StoryblokComponentData } from "@/components/Page";

export default function SectionWrapper({ blok, children }: Readonly<{ blok: SectionStoryblok; data?: StoryblokComponentData; children: React.ReactNode }>) {
  const sectionName = typeof blok.name === "string" ? blok.name : "";
  const topItems = Array.from({ length: 30 }, (_, i) => `${blok._uid}-top-${i}`);
  const bottomItems = Array.from({ length: 30 }, (_, i) => `${blok._uid}-bottom-${i}`);

  const topScrollRef = useRef<HTMLDivElement>(null);
  const bottomScrollRef = useRef<HTMLDivElement>(null);
  const sectionRef = useRef<HTMLElement>(null);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsInView(entry.isIntersecting);
      },
      { threshold: 0 }
    );

    const sectionElement = sectionRef.current;
    if (sectionElement) {
      observer.observe(sectionElement);
    }

    return () => {
      if (sectionElement) {
        observer.unobserve(sectionElement);
      }
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (isInView) {
        const scrollSpeedFactor = 0.2;
        const scrollAmount = window.scrollY * scrollSpeedFactor;

        if (topScrollRef.current) {
          topScrollRef.current.scrollLeft = scrollAmount;
        }
        if (bottomScrollRef.current) {
          bottomScrollRef.current.scrollLeft = scrollAmount;
        }
      }
    };

    if (isInView) {
      window.addEventListener("scroll", handleScroll);
    } else {
      window.removeEventListener("scroll", handleScroll);
    }

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [isInView]);

  return (
    <section
      ref={sectionRef}
      {...storyblokEditable(blok)}
      id={createSlug(sectionName)}
      data-has-moving-name={blok.show_moving_name ? "true" : "false"}
      className={cn(
        "relative flex flex-col gap-6 p-6 py-16 lg:p-16 first:pt-6",
        blok.show_moving_name && "my-16 moving-name"
      )}
    >
      {blok.show_moving_name && (
        <div
          ref={topScrollRef}
          className="font-mono absolute top-0 -translate-y-1/2 left-0 w-full whitespace-nowrap overflow-hidden flex items-center justify-center gap-4"
        >
          {topItems.map((itemKey) => (
              <div key={itemKey} className="text-md uppercase flex gap-4">
                {sectionName}
                <span>•</span>
              </div>
            ))}
        </div>
      )}
      {children}
      {blok.show_moving_name && (
        <div
          ref={bottomScrollRef}
          className="font-mono absolute bottom-0 translate-y-1/2 left-0 w-full whitespace-nowrap overflow-hidden flex items-center justify-center gap-4"
        >
          {bottomItems.map((itemKey) => (
              <div key={itemKey} className="text-md uppercase flex gap-4">
                {sectionName}
                <span>•</span>
              </div>
            ))}
        </div>
      )}
    </section>
  );
};